import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiAlertOutline, mdiAccountSwitchOutline } from '@mdi/js'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { Badge } from '../ui/badge'
import { AppIcon } from '../ui/icon'
import { EmptyState } from '../ui/empty-state'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { useAuth } from '../../contexts/AuthContext'
import { useCopyImpersonationLink } from '../impersonation/useCopyImpersonationLink'
import {
  useAdminUsageUserSessions,
  type AdminUsageSession,
  type UsagePeriod,
} from '../../hooks/use-admin-usage'

interface Props {
  userUid?: string
  period: UsagePeriod
  /** YYYY-MM-DD, the day read (or any day of the month read). */
  day: string
  onClose: () => void
}

interface Range {
  from: string
  to: string
  /** Start of each timeline slot (hours of the day, or days of the month). */
  slots: Date[]
  slotOf: (hour: Date) => number
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * The timeline covers the period on screen: the 24 hours of the day, or the
 * days of the month (each day summing its hours). Everything is UTC, like the
 * usage buckets.
 */
function rangeFor(period: UsagePeriod, day: string): Range {
  if (period === 'month') {
    const [y, m] = day.split('-').map(Number)
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate()
    const slots = Array.from({ length: daysInMonth }, (_, i) => new Date(Date.UTC(y, m - 1, i + 1)))
    return {
      from: `${y}-${pad(m)}-01`,
      to: `${y}-${pad(m)}-${pad(daysInMonth)}`,
      slots,
      slotOf: (hour) => (hour.getUTCFullYear() === y && hour.getUTCMonth() === m - 1 ? hour.getUTCDate() - 1 : -1),
    }
  }
  const start = new Date(`${day}T00:00:00Z`)
  const slots = Array.from({ length: 24 }, (_, i) => new Date(start.getTime() + i * 3600_000))
  return {
    from: day,
    to: day,
    slots,
    slotOf: (hour) => {
      const i = Math.floor((hour.getTime() - start.getTime()) / 3600_000)
      return i >= 0 && i < 24 ? i : -1
    },
  }
}

/** One count per slot for a session. */
function slotCounts(session: AdminUsageSession, range: Range): number[] {
  const counts = new Array(range.slots.length).fill(0)
  for (const h of session.hours) {
    const i = range.slotOf(new Date(h.hour))
    if (i >= 0) counts[i] += h.count
  }
  return counts
}

/** A cell's tint grows with its share of the busiest cell. */
function intensityClass(count: number, max: number): string {
  if (count <= 0) return 'bg-muted'
  const ratio = max > 0 ? count / max : 1
  if (ratio > 0.75) return 'bg-primary'
  if (ratio > 0.4) return 'bg-primary/70'
  if (ratio > 0.15) return 'bg-primary/45'
  return 'bg-primary/25'
}

export function UsageUserSessionsSheet({ userUid, period, day, onClose }: Props) {
  const { t, i18n } = useTranslation('admin')
  const range = useMemo(() => rangeFor(period, day), [period, day])
  const { data, isLoading, error } = useAdminUsageUserSessions(userUid, range.from, range.to)
  const detail = data?.data
  const { user: me } = useAuth()
  const impersonation = useCopyImpersonationLink()
  const displayName = detail?.user_name || detail?.user_email || t('usage.sessions.title')

  const dateTime = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short', timeZone: 'UTC' }),
    [i18n.language],
  )
  const fmt = (v?: string) => (v ? dateTime.format(new Date(v)) : '—')

  const rows = useMemo(() => {
    const sessions = detail?.sessions ?? []
    return sessions.map((s) => ({ session: s, counts: slotCounts(s, range) }))
  }, [detail, range])
  const max = Math.max(0, ...rows.flatMap((r) => r.counts))
  // Sessions (not machine tokens) active in the same hour: overlapping
  // sessions are the shared-account signal. A day of the month view shows its
  // busiest hour, the same definition as sessions_concurrent.
  const concurrent = useMemo(() => {
    const perHour = new Map<string, number>()
    for (const s of detail?.sessions ?? []) {
      if (s.kind !== 'session') continue
      for (const h of s.hours) {
        if (h.count > 0) perHour.set(h.hour, (perHour.get(h.hour) ?? 0) + 1)
      }
    }
    const out = new Array(range.slots.length).fill(0)
    for (const [hour, n] of perHour) {
      const i = range.slotOf(new Date(hour))
      if (i >= 0) out[i] = Math.max(out[i], n)
    }
    return out
  }, [detail, range])
  const slotLabel = (d: Date, i: number) =>
    period === 'month' ? (i % 5 === 0 ? String(d.getUTCDate()) : '') : (i % 3 === 0 ? pad(d.getUTCHours()) : '')
  const slotTitle = (d: Date) =>
    period === 'month' ? d.toISOString().slice(0, 10) : `${pad(d.getUTCHours())}:00 UTC`

  const networkLabel = (s: AdminUsageSession) =>
    [s.network.provider, [s.network.city, s.network.country_code].filter(Boolean).join(', ')].filter(Boolean).join(' · ')

  const gridStyle = { gridTemplateColumns: `repeat(${range.slots.length}, minmax(0, 1fr))` }

  return (
    <Sheet open={!!userUid} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto" data-testid="usage-sessions-sheet">
        <SheetHeader>
          <SheetTitle data-testid="usage-sessions-title">
            {/* The contact page, in the user's own organization (spec 2026-10-04-03). */}
            {detail?.organization_code && userUid ? (
              <Link
                to="/$org/users/$userUid"
                params={{ org: detail.organization_code, userUid }}
                className="hover:underline"
                title={t('impersonation.open_contact')}
                data-testid="usage-sessions-contact-link"
              >
                {displayName}
              </Link>
            ) : displayName}
          </SheetTitle>
          <SheetDescription>
            {detail?.user_name ? `${detail.user_email} · ` : ''}
            {t(period === 'month' ? 'usage.sessions.range_month' : 'usage.sessions.range_day', { from: range.from, to: range.to })}
          </SheetDescription>
          {me?.is_platform_admin && detail && userUid && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={impersonation.isPending}
                onClick={() => { void impersonation.copy(userUid, detail.organization_code || undefined) }}
                data-testid="usage-sessions-impersonate"
              >
                <AppIcon path={mdiAccountSwitchOutline} size="sm" className="mr-1" />
                {t('impersonation.action')}
              </Button>
              {impersonation.copied && (
                <span className="text-xs text-muted-foreground" data-testid="usage-sessions-impersonate-copied">
                  {t('impersonation.copied_inline')}
                </span>
              )}
              {impersonation.errorKey && (
                <span className="text-xs text-destructive" data-testid="usage-sessions-impersonate-error">
                  {t(impersonation.errorKey)}
                </span>
              )}
            </div>
          )}
          {impersonation.dialog}
        </SheetHeader>

        <div className="px-4 pb-6 space-y-6">
          {error && (
            <div className="text-sm text-destructive" data-testid="usage-sessions-error">{t('usage.load_error')}</div>
          )}
          {isLoading && <div className="text-sm text-muted-foreground">{t('usage.loading')}</div>}

          {detail && detail.alerts.length > 0 && (
            <div className="space-y-2" data-testid="usage-alerts">
              {detail.alerts.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-sm rounded-md border border-warning/25 bg-warning/10 px-3 py-2">
                  <AppIcon path={mdiAlertOutline} size="sm" className="text-warning" />
                  <span className="text-foreground">{t(`usage.reason.${a.reason}`, { defaultValue: a.reason })}</span>
                  <span className="text-muted-foreground ml-auto">{fmt(a.created_at)}</span>
                </div>
              ))}
            </div>
          )}

          {detail && rows.length === 0 && (
            <EmptyState variant="inline" title={t('usage.sessions.empty')} data-testid="usage-sessions-empty" />
          )}

          {rows.length > 0 && (
            <section className="space-y-2" data-testid="usage-timeline">
              <h3 className="text-sm font-medium text-foreground">
                {t(period === 'month' ? 'usage.sessions.timeline_month' : 'usage.sessions.timeline_day')}
              </h3>
              <div className="grid grid-cols-[14rem_1fr] gap-x-3 gap-y-1.5 items-center text-xs">
                <div />
                <div className="grid gap-px text-muted-foreground" style={gridStyle}>
                  {range.slots.map((d, i) => <div key={i} className="text-[10px] leading-none">{slotLabel(d, i)}</div>)}
                </div>

                <div className="font-medium text-foreground">{t(period === 'month' ? 'usage.sessions.concurrent_month' : 'usage.sessions.concurrent')}</div>
                <div className="grid gap-px" style={gridStyle} data-testid="usage-timeline-concurrent">
                  {concurrent.map((n, i) => (
                    <div
                      key={i}
                      title={`${slotTitle(range.slots[i])}: ${n}`}
                      className={cn(
                        'h-5 rounded-[2px] flex items-center justify-center text-[10px] tabular-nums',
                        n >= 3 ? 'bg-destructive/80 text-white' : n === 2 ? 'bg-warning/70 text-foreground' : n === 1 ? 'bg-muted text-muted-foreground' : 'bg-muted/40',
                      )}
                    >
                      {n > 0 ? n : ''}
                    </div>
                  ))}
                </div>

                {rows.map(({ session, counts }) => (
                  <div key={session.uid} className="contents">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1 text-foreground truncate">
                        <Badge variant={session.kind === 'machine' ? 'secondary' : 'outline'} className="text-[10px] px-1 py-0">
                          {t(`usage.kind.${session.kind}`)}
                        </Badge>
                        <span className="truncate font-mono">{session.network.ip || session.name || [session.token_type, session.uid.slice(0, 8)].filter(Boolean).join(' ')}</span>
                      </div>
                      <div className="text-muted-foreground truncate">{networkLabel(session) || '—'}</div>
                    </div>
                    <div className="grid gap-px" style={gridStyle} data-testid={`usage-timeline-${session.uid}`}>
                      {counts.map((c, i) => (
                        <div
                          key={i}
                          title={`${slotTitle(range.slots[i])}: ${c}`}
                          className={cn('h-5 rounded-[2px]', intensityClass(c, max))}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {rows.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">{t('usage.sessions.details')}</h3>
              <div className="rounded-lg border border-border overflow-x-auto">
                <Table data-testid="usage-sessions-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('usage.sessions.column.kind')}</TableHead>
                      <TableHead>{t('usage.sessions.column.created')}</TableHead>
                      <TableHead>{t('usage.sessions.column.last_used')}</TableHead>
                      <TableHead>{t('usage.sessions.column.ended')}</TableHead>
                      <TableHead>{t('usage.sessions.column.ip')}</TableHead>
                      <TableHead>{t('usage.sessions.column.network')}</TableHead>
                      <TableHead className="text-right">{t('usage.sessions.column.calls')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map(({ session }) => (
                      <TableRow key={session.uid} data-testid={`usage-session-${session.uid}`}>
                        <TableCell>
                          <div>{t(`usage.kind.${session.kind}`)}</div>
                          {session.name && <div className="text-xs text-muted-foreground">{session.name}</div>}
                          {session.organization_code && <div className="text-xs font-mono text-muted-foreground">{session.organization_code}</div>}
                        </TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">{fmt(session.created_at)}</TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">{fmt(session.last_used_at)}</TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">{fmt(session.ended_at)}</TableCell>
                        <TableCell className="font-mono text-xs">{session.network.ip || '—'}</TableCell>
                        <TableCell>
                          <div>{session.network.provider || '—'}</div>
                          <div className="text-xs text-muted-foreground">
                            {[session.network.city, session.network.country].filter(Boolean).join(', ')}
                          </div>
                          {session.user_agent && (
                            <div className="text-xs text-muted-foreground truncate max-w-64" title={session.user_agent}>{session.user_agent}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{session.api_calls}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">{t('usage.sessions.utc_note')}</p>
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
